import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ClipboardList, Edit2, X, Truck, MapPin, Check,
  Clock, Ban, ChevronDown, ChevronUp, Zap, Lock,
  AlertTriangle, ArrowRight,
} from 'lucide-react';
import { myOrdersAPI, productsAPI } from '../api';

const STATUS_META = {
  pending:   { color: '#F5A800', bg: 'rgba(245,168,0,0.1)',   border: 'rgba(245,168,0,0.3)',  label: 'PENDING',   icon: Clock  },
  fulfilled: { color: '#22c55e', bg: 'rgba(34,197,94,0.08)',  border: 'rgba(34,197,94,0.25)', label: 'FULFILLED', icon: Check  },
  cancelled: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.25)', label: 'CANCELLED', icon: Ban    },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: m.bg, border: `1px solid ${m.border}`, color: m.color,
      fontFamily: 'var(--font-condensed)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.15em', textTransform: 'uppercase',
      padding: '3px 9px',
    }}>
      <Icon size={9} /> {m.label}
    </span>
  );
}

/* ─── Edit Modal ──────────────────────────────────────────────────────────── */
function EditModal({ order, products, onClose, onSave }) {
  const [qtys, setQtys]         = useState(() => {
    const map = {};
    (order.items || []).forEach(i => { map[i.name] = i.quantity; });
    return map;
  });
  const [delivery, setDelivery] = useState(order.delivery_type || 'pickup');
  const [notes, setNotes]       = useState(order.notes || '');
  const [saving, setSaving]     = useState(false);

  const setQty = (name, qty) => setQtys(q => ({ ...q, [name]: qty }));

  const regularProducts = products.filter(p => !p.is_limited);
  const limitedProducts = products.filter(p => p.is_limited);

  const orderItems = products
    .filter(p => (qtys[p.name] || 0) > 0)
    .map(p => ({ name: p.name, quantity: qtys[p.name], price: parseFloat(p.price) }));

  const total = orderItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const meetsMin = total >= 5000;

  const handleSave = async () => {
    if (orderItems.length === 0) { toast.error('Select at least one item'); return; }
    if (!meetsMin) { toast.error('Minimum order is $5,000'); return; }
    setSaving(true);
    try {
      const res = await myOrdersAPI.edit(order.id, { items: orderItems, delivery_type: delivery, notes });
      toast.success('Order updated — management has been notified');
      onSave(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to update order';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        style={{
          background: 'var(--bce-bg-card)',
          border: '1px solid var(--bce-border)',
          borderTop: '3px solid var(--bce-gold)',
          width: '100%', maxWidth: 580,
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--bce-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.2em', color: 'rgba(245,168,0,0.5)', textTransform: 'uppercase', marginBottom: 2 }}>
              Edit Order #{order.id}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--bce-white)' }}>
              MODIFY ORDER
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--bce-text-faint)', padding: 6, cursor: 'pointer', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Delivery */}
          <div>
            <div className="bce-section-label" style={{ marginBottom: '0.75rem' }}><span>Delivery Method</span></div>
            <div style={{ display: 'flex', gap: '1px', background: 'var(--bce-border)' }}>
              {[
                { v: 'pickup',   label: 'Pickup',       icon: MapPin, sub: 'Come to our location.' },
                { v: 'delivery', label: 'GTY Delivery',  icon: Truck,  sub: 'We deliver to you.' },
              ].map(opt => (
                <div key={opt.v} onClick={() => setDelivery(opt.v)}
                  style={{
                    flex: 1, padding: '0.9rem 1rem', cursor: 'pointer',
                    background: delivery === opt.v ? '#1a1200' : 'var(--bce-bg-card)',
                    border: delivery === opt.v ? '1px solid var(--bce-gold)' : '1px solid transparent',
                    display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s',
                    position: 'relative',
                  }}
                >
                  {delivery === opt.v && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'var(--bce-gold)' }} />}
                  <opt.icon size={16} color={delivery === opt.v ? 'var(--bce-gold)' : 'var(--bce-text-faint)'} />
                  <div>
                    <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: delivery === opt.v ? 'var(--bce-white)' : 'var(--bce-text)' }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--bce-text-faint)' }}>{opt.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Regular products */}
          <div>
            <div className="bce-section-label" style={{ marginBottom: '0.75rem' }}><span>Products</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)' }}>
              {regularProducts.map(p => {
                const qty = qtys[p.name] || 0;
                const active = qty > 0;
                return (
                  <div key={p.name} style={{
                    background: active ? 'var(--bce-bg-elevated)' : 'var(--bce-bg-card)',
                    padding: '0.85rem 1.25rem',
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    borderLeft: `3px solid ${active ? 'var(--bce-gold)' : 'transparent'}`,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--bce-white)' }}>
                        {p.name}
                      </div>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--bce-gold)' }}>
                        ${parseFloat(p.price).toLocaleString()}
                      </span>
                    </div>
                    <select className="bce-select" value={qty}
                      onChange={e => setQty(p.name, parseInt(e.target.value))}
                      style={{ width: 72, padding: '6px 24px 6px 8px' }}
                    >
                      {[0,1,2,3,4,5,10,15,20,25,50,75,100,150,200,250,300,400,500,750,1000,1250,1500].map(q => <option key={q} value={q}>{q}</option>)}
                    </select>
                    {active && (
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--bce-gold)', minWidth: 80, textAlign: 'right' }}>
                        ${(qty * parseFloat(p.price)).toLocaleString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Limited products */}
          {limitedProducts.length > 0 && (
            <div>
              <div className="bce-section-label" style={{ marginBottom: '0.75rem' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Zap size={10} color="var(--bce-gold)" /> Limited Items</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {limitedProducts.map(p => {
                  const qty = qtys[p.name] || 0;
                  const active = qty > 0;
                  return (
                    <div key={p.name} style={{
                      background: active ? '#1a1200' : 'var(--bce-bg-card)',
                      border: `1px solid ${active ? 'rgba(245,168,0,0.4)' : 'rgba(245,168,0,0.12)'}`,
                      padding: '0.85rem 1.25rem',
                      display: 'flex', alignItems: 'center', gap: '1rem',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--bce-white)' }}>
                            {p.name}
                          </span>
                          <span style={{ fontSize: 8, fontFamily: 'var(--font-condensed)', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', background: 'var(--bce-gold)', color: '#000', padding: '1px 5px' }}>
                            LIMITED
                          </span>
                        </div>
                        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--bce-gold)' }}>
                          ${parseFloat(p.price).toLocaleString()}
                        </span>
                      </div>
                      <button
                        onClick={() => setQty(p.name, active ? 0 : 1)}
                        style={{
                          padding: '6px 14px',
                          background: active ? 'var(--bce-gold)' : 'transparent',
                          border: `1px solid ${active ? 'var(--bce-gold)' : 'rgba(245,168,0,0.3)'}`,
                          color: active ? '#000' : 'var(--bce-gold)',
                          fontFamily: 'var(--font-condensed)', fontSize: 11,
                          fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                          cursor: 'pointer', transition: 'all 0.15s',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        {active ? <><Lock size={10} /> Added</> : 'Add'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bce-field">
            <label>Notes</label>
            <textarea
              className="bce-textarea" rows={2} value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any special requests..."
              style={{ minHeight: 60 }}
            />
          </div>

          {/* Total + save */}
          <div style={{
            background: 'var(--bce-bg)',
            border: `1px solid var(--bce-border)`,
            borderTop: `2px solid ${meetsMin ? 'var(--bce-gold)' : 'var(--bce-danger)'}`,
            padding: '1rem 1.25rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div>
              <div className="bce-label" style={{ marginBottom: 2 }}>New Total</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: meetsMin ? 'var(--bce-gold)' : 'var(--bce-danger)', lineHeight: 1 }}>
                ${total.toLocaleString()}
              </div>
              {!meetsMin && total > 0 && (
                <div style={{ fontSize: 10, color: 'var(--bce-danger)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.08em', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={10} /> Minimum $5,000
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="bce-btn bce-btn-ghost" onClick={onClose} style={{ fontSize: 12 }}>
                Cancel
              </button>
              <button
                className="bce-btn bce-btn-gold"
                onClick={handleSave}
                disabled={saving || !meetsMin || orderItems.length === 0}
                style={{ opacity: (!meetsMin || orderItems.length === 0) ? 0.45 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {saving ? 'Saving...' : <><Check size={14} /> Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Order card ──────────────────────────────────────────────────────────── */
function OrderCard({ order, products, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing]   = useState(false);

  const date = new Date(order.created_at).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const time = new Date(order.created_at).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit',
  });

  const handleSave = (updated) => {
    setEditing(false);
    onUpdated(updated);
  };

  return (
    <>
      <motion.div
        layout
        style={{
          background: 'var(--bce-bg-card)',
          border: '1px solid var(--bce-border)',
          borderLeft: `3px solid ${STATUS_META[order.status]?.color || 'var(--bce-gold)'}`,
          overflow: 'hidden',
        }}
      >
        {/* Card header */}
        <div style={{
          padding: '1.1rem 1.5rem',
          display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, letterSpacing: '0.15em', color: 'var(--bce-text-faint)', textTransform: 'uppercase' }}>
                Order #{order.id}
              </span>
              <StatusBadge status={order.status} />
              <span style={{ fontSize: 10, color: 'var(--bce-text-faint)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.05em' }}>
                {date} · {time}
              </span>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--bce-gold)' }}>
              ${parseFloat(order.total_value).toLocaleString()}
            </div>
            <div style={{ fontSize: 11, color: 'var(--bce-text-faint)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}</span>
              <span>·</span>
              {order.delivery_type === 'delivery'
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Truck size={10} /> GTY Delivery</span>
                : <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={10} /> Pickup</span>
              }
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {order.status === 'pending' && (
              <button
                className="bce-btn bce-btn-ghost bce-btn-sm"
                onClick={() => setEditing(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}
              >
                <Edit2 size={12} /> Edit
              </button>
            )}
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                background: 'none', border: '1px solid rgba(255,255,255,0.08)',
                color: 'var(--bce-text-faint)', padding: '6px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
              }}
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>

        {/* Expanded items */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden' }}
            >
              <div style={{ borderTop: '1px solid var(--bce-border)', padding: '0.85rem 1.5rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(order.items || []).map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--bce-gold)', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: 'var(--bce-text)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.05em' }}>
                        {item.name}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--bce-text-faint)' }}>× {item.quantity}</span>
                    </div>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', color: 'var(--bce-gold)' }}>
                      ${(item.price * item.quantity).toLocaleString()}
                    </span>
                  </div>
                ))}
                {order.notes && (
                  <div style={{ marginTop: 6, padding: '0.6rem 0.85rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', fontSize: 12, color: 'var(--bce-text-faint)', lineHeight: 1.6 }}>
                    <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--bce-text-faint)', display: 'block', marginBottom: 3 }}>Notes</span>
                    {order.notes}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {editing && (
          <EditModal
            order={order}
            products={products}
            onClose={() => setEditing(false)}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────────── */
export default function MyOrders() {
  const [orders, setOrders]   = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, productsRes] = await Promise.all([
        myOrdersAPI.list(),
        productsAPI.list(),
      ]);
      setOrders(ordersRes.data || []);
      setProducts(productsRes.data || []);
    } catch (err) {
      toast.error('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdated = (updatedOrder) => {
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
  };

  const FILTERS = [
    { key: 'all',       label: 'All Orders' },
    { key: 'pending',   label: 'Pending'    },
    { key: 'fulfilled', label: 'Fulfilled'  },
    { key: 'cancelled', label: 'Cancelled'  },
  ];

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const pendingCount = orders.filter(o => o.status === 'pending').length;

  return (
    <div className="bce-page" style={{ background: 'var(--bce-bg)', paddingBottom: '5rem' }}>
      <div className="bce-container-sm" style={{ paddingTop: '4rem' }}>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
          style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}
        >
          <div style={{ width: 30, height: 2, background: 'var(--bce-gold)' }} />
          <span className="bce-label">Order History</span>
        </motion.div>

        <div style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
          <motion.h1
            className="bce-display"
            initial={{ y: '100%' }} animate={{ y: 0 }}
            transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontSize: 'clamp(2.5rem, 7vw, 4.5rem)', color: 'var(--bce-white)' }}
          >
            MY ORDERS
          </motion.h1>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '2.5rem', flexWrap: 'wrap' }}
        >
          <p style={{ color: 'var(--bce-text)', lineHeight: 1.8, fontSize: 14, margin: 0 }}>
            View and manage your order history.
            {pendingCount > 0 && (
              <span style={{ color: 'var(--bce-gold)' }}> {pendingCount} pending order{pendingCount > 1 ? 's' : ''} can still be edited.</span>
            )}
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          style={{ display: 'flex', gap: '1px', background: 'var(--bce-border)', marginBottom: '1.5rem' }}
        >
          {FILTERS.map(f => {
            const count = f.key === 'all' ? orders.length : orders.filter(o => o.status === f.key).length;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)}
                style={{
                  flex: 1, padding: '0.75rem 1rem',
                  background: filter === f.key ? '#1a1200' : 'var(--bce-bg-card)',
                  border: filter === f.key ? '1px solid rgba(245,168,0,0.35)' : '1px solid transparent',
                  color: filter === f.key ? 'var(--bce-gold)' : 'var(--bce-text-faint)',
                  fontFamily: 'var(--font-condensed)', fontSize: 12,
                  fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                  cursor: 'pointer', transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                {f.label}
                {count > 0 && (
                  <span style={{
                    background: filter === f.key ? 'var(--bce-gold)' : 'rgba(255,255,255,0.08)',
                    color: filter === f.key ? '#000' : 'var(--bce-text-faint)',
                    fontSize: 9, padding: '1px 5px', fontFamily: 'var(--font-condensed)',
                    fontWeight: 700, minWidth: 18, textAlign: 'center',
                  }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="bce-loading"><div className="bce-spinner" /> Loading your orders...</div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '3.5rem 2rem', textAlign: 'center',
              background: 'var(--bce-bg-card)',
              border: '1px solid var(--bce-border)',
            }}
          >
            <ClipboardList size={36} color="var(--bce-text-faint)" style={{ margin: '0 auto 1rem', display: 'block' }} />
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--bce-text-faint)', marginBottom: '0.5rem' }}>
              {filter === 'all' ? 'No orders yet' : `No ${filter} orders`}
            </div>
            {filter === 'all' && (
              <div style={{ fontSize: 13, color: 'var(--bce-text-faint)', lineHeight: 1.7 }}>
                Head to the order page to place your first order.
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)' }}
          >
            {filtered.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                products={products}
                onUpdated={handleUpdated}
              />
            ))}
          </motion.div>
        )}

        {/* Place new order CTA */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            style={{ marginTop: '2rem', textAlign: 'right' }}
          >
            <a href="/order" className="bce-btn bce-btn-ghost"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              Place New Order <ArrowRight size={13} />
            </a>
          </motion.div>
        )}

      </div>
    </div>
  );
}
